import React, { useState } from 'react';
import { Mail, Phone, MessageSquare, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import useScrollAnimation from '../hooks/useScrollAnimation';

export const Contact = () => {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [formRef, formVisible] = useScrollAnimation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: false,
    consultationTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data
      const submissionData = {
        access_key: process.env.REACT_APP_WEB3FORMS_KEY,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        whatsapp_preferred: formData.whatsapp ? 'Yes' : 'No',
        consultation_time: formData.consultationTime,
        message: formData.message,
        subject: `New ${formData.consultationTime || 'Consultation'} Request from ${formData.name}`,
        from_name: 'HEATH & Wellbeing Contact Form',
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSuccess(true);
        toast.success('Message sent successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          whatsapp: false,
          consultationTime: '',
          message: ''
        });
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        toast.error(result.message || 'Failed to send message. Please try again.');
        console.error('Form submission failed:', result);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="contact-grid">
          <div className="contact-info">
            <h2 
              ref={titleRef}
              className={`display-medium animate-slide-right ${titleVisible ? 'is-visible' : ''}`}
            >
              Let's Start Your Journey
            </h2>
            <p className="body-large">
              Ready to build from the inside out? Book a consultation and let's 
              create a path that works for you.
            </p>

            <div className="whatsapp-cta">
              <a 
                href="https://wa.me/61478694929"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_heath-wellbeing/artifacts/7dalcgwb_whatsapp-icon-png-13.png"
                  alt="WhatsApp"
                  className="whatsapp-icon-img"
                />
                <div className="whatsapp-text">
                  <span className="whatsapp-title">Message on WhatsApp</span>
                  <span className="whatsapp-subtitle">Get instant response</span>
                </div>
              </a>
            </div>

            <div className="contact-divider">
              <span>or fill the form below</span>
            </div>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="heading-3">Email</h4>
                  <p className="body-medium">Get in touch via email</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="heading-3">Phone / WhatsApp</h4>
                  <p className="body-medium">Let us know your preference</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Clock size={24} />
                </div>
                <div>
                  <h4 className="heading-3">Flexible Schedule</h4>
                  <p className="body-medium">Morning, afternoon, or evening</p>
                </div>
              </div>
            </div>

            <div className="contact-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_heath-wellbeing/artifacts/78brvv4a_IMG_6567.jpeg"
                alt="Community gathering"
                className="contact-image"
              />
            </div>
          </div>

          <div className="contact-form-wrapper">
            {isSuccess ? (
              <div className="success-message">
                <div className="success-icon">
                  <Check size={48} />
                </div>
                <h3 className="heading-2">Thank You!</h3>
                <p className="body-large">
                  We've received your message and will be in touch soon to discuss 
                  your journey to sustainable strength.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    minLength={2}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    minLength={10}
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="form-input"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label htmlFor="whatsapp" className="checkbox-label">
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="checkbox"
                      checked={formData.whatsapp}
                      onChange={handleChange}
                      className="form-checkbox"
                    />
                    <MessageSquare size={18} />
                    <span>Contact me via WhatsApp</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="consultationTime" className="form-label">
                    Preferred Consultation Time *
                  </label>
                  <select
                    id="consultationTime"
                    name="consultationTime"
                    required
                    value={formData.consultationTime}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select a time...</option>
                    <option value="morning">Morning (9 AM - 12 PM)</option>
                    <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                    <option value="evening">Evening (5 PM - 8 PM)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    minLength={10}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your goals and what you're looking to achieve..."
                    className="form-input form-textarea"
                    rows={5}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary btn-large btn-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Book Your Consultation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
